/**
 * The view layer: every screen render function (JSX -> SafeHtml) plus the render
 * dispatcher. Pure rendering — reads signals and emits data-action attributes;
 * the root delegates in init.ts bridge those to the loaders. Never imports
 * loaders/init, so it stays a lower layer (app.tsx split, HS2-ZB7GJN).
 */
import './index.css';

import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import { type SafeHtml } from 'kerfjs';

import { formAcceptsAnswers } from '../../../shared/contract/forms.js';
import type { DemandNote } from '../../../shared/demand-notes.js';
import { DemandSummarySegment } from '../../components/demand-summary/index.js';
import { QuestionsDialog } from '../../components/forms.js';
import { PageLoading } from '../../components/page-loading.js';
import {
  editorSegment,
  type FormRow,
  type IntakeDoc,
  type IntakeLine,
  questionsDraft,
  reopenUpdateOpen,
  returnDialogOpen,
  workspace,
} from '../../data/state.js';
import {
  demandDetailsReadOnly,
  demandNotesReadOnly,
  demandReadOnly,
  demandRejectionState,
  isRequester,
} from '../../lib/context.js';
import { editorTransferTarget } from '../../lib/editor-transfer-target.js';
/**
 * The SPA shell for Phase 1 so far: the first-sign-in "create your organization"
 * flow (F1), an empty Demands landing with an explainer, and the Settings pages.
 * Feature screens (demand workspace, analysis, chat) mount into the same shell in
 * their own tickets. State lives in module signals; structure re-renders on auth
 * and view changes, values bind directly (kerf idiom).
 */
import { DocumentPreviewDialog } from './components/document-preview-dialog/index.js';
import { FilesSegment } from './components/files-segment.js';
import { InfoSegment } from './components/info-segment.js';
import { LinesSegment } from './components/lines-segment.js';
import { QuotesSegment } from './components/quotes-segment.js';
import { ReopenUpdateDialog } from './components/reopen-update-dialog.js';

export { DemandEditorBanners } from './components/demand-editor-banners.js';
export { DemandEditorSubHeader } from './components/demand-editor-sub-header.js';
export { DemandSegmentsControl } from './components/demand-segments-control.js';
export { Onboarding } from './components/onboarding.js';

export function DemandEditor(): SafeHtml {
  const ws = workspace.value;
  if (ws === null)
    return (
      <section class="demand-editor page-loading-host">
        <PageLoading label="Loading demand" />
      </section>
    );
  const demand = ws.demand as {
    id: string;
    title: string;
    status: string;
    category: string | null;
    purpose: string | null;
    neededBy: string | null;
    supportingNote: string | null;
    requesterMemberId: string | null;
    /** The responsible staff member (FR-5.20); absent on an older cached payload. */
    assigneeMemberId?: string | null;
    assigneeName?: string | null;
  };
  const lines = ((ws.lineItems ?? []) as IntakeLine[]).slice().sort((a, b) => a.lineNumber - b.lineNumber);
  const filesByKind = (ws.filesByKind ?? {}) as Record<string, IntakeDoc[]>;
  const forms = (ws.forms ?? []) as FormRow[];
  const requester = isRequester();
  const forRole = (form: FormRow): boolean =>
    form.schema.targetRole === undefined ||
    form.schema.targetRole === 'any' ||
    form.schema.targetRole === (requester ? 'requester' : 'reviewer');
  const openForms = forms.filter((form) => form.status === 'open' && forRole(form));
  // A skipped pricing-check clarification opened from Summary's "Answer and
  // Re-check…" is answered in the same dialog (HS2-54N88P).
  const reopened = forms.find(
    (form) =>
      form.id === questionsDraft.value?.formId && form.status !== 'open' && formAcceptsAnswers(form) && forRole(form),
  );
  const dialogForms = reopened === undefined ? openForms : [reopened, ...openForms];
  const seg = editorSegment.value;
  // A cancelled demand is read-only (HS2-ZXQAM6): no drop target, and each segment
  // renders without its mutating controls.
  const readOnly = demandReadOnly();
  // Details stay editable while decided; decisions stay open until closed (HS2-Y50H8E).
  const detailsReadOnly = demandDetailsReadOnly();
  // The whole content area is a file drop target on the upload segments (HS2-17E1DF),
  // not just the inner box; the overlay hint reflects what the active segment accepts.
  const transferTarget = editorTransferTarget(seg, readOnly);

  return (
    <section class="demand-editor" data-droppable={String(transferTarget !== null)}>
      {/* Status banners (questions + notice) render in the header area, not here
          (HS2-BFHGES: DemandEditorBanners). The questions dialog stays with the editor. */}
      <QuestionsDialog openForms={dialogForms} />
      {returnDialogOpen.value ? (
        <DialogSurface
          label="Return to Requester"
          lightDismiss
          className="return-demand-dialog"
          rootAttributes={{ 'data-auto-open-dialog': '' }}
          footer={
            <>
              <Button
                tone="neutral"
                size="default"
                emphasis="plain"
                action="demand-return-close"
                rootAttributes={{ 'data-dialog-close': '' }}
              >
                Cancel
              </Button>
              <Button emphasis="primary" size="default" tone="brand" action="demand-return-submit">
                Return Demand
              </Button>
            </>
          }
        >
          <List gap="m">
            <Text variant="p">
              Explain what the requester should change. Add one question per line if you need structured answers.
              Nothing is rejected; staff can keep working while it is returned.
            </Text>
            <TextField multiline label="Note to Requester" name="return-note" rows={3} />
            <TextField multiline label="Questions (Optional, One per Line)" name="return-questions" rows={4} />
          </List>
        </DialogSurface>
      ) : null}
      <DocumentPreviewDialog />
      {reopenUpdateOpen.value ? <ReopenUpdateDialog rejected={demandRejectionState()?.rejected ?? 0} /> : null}

      {/* The section switcher lives in the main toolbar; pricing checks start from Quotes. */}
      {seg === 'info' ? (
        <InfoSegment demand={demand} readOnly={detailsReadOnly} />
      ) : seg === 'lines' ? (
        <LinesSegment demandId={demand.id} lines={lines} readOnly={readOnly} />
      ) : seg === 'quotes' ? (
        <QuotesSegment readOnly={readOnly} filesByKind={filesByKind} />
      ) : seg === 'files' ? (
        <FilesSegment
          filesByKind={filesByKind}
          notes={(ws.notes ?? []) as DemandNote[]}
          readOnly={readOnly}
          notesReadOnly={demandNotesReadOnly()}
        />
      ) : (
        <DemandSummarySegment />
      )}
    </section>
  );
}
